/**
 * AdminIdleLockSettings — Owner-only panel inside /admin/settings/site that
 * persists the global admin idle-lock timeout (5/10/15 min).
 *
 * Backend RBAC enforces Owner-only on PUT, so a non-Owner admin will get
 * a clean 403 when they hit Save (we surface it as a toast).
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import { adminIdleConfigGet, adminIdleConfigSet } from "../../lib/api";

const OPTIONS = [
  { value: 5,  label: "5 minutes",  hint: "Strict — for shared workstations." },
  { value: 10, label: "10 minutes", hint: "Default — balanced for daily editorial use." },
  { value: 15, label: "15 minutes", hint: "Lenient — for trusted office terminals." },
];

export const AdminIdleLockSettings = ({ token, userToken }) => {
  const [value, setValue] = useState(10);
  const [saved, setSaved] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await adminIdleConfigGet(token || null, userToken || null);
      setValue(cfg.timeout_minutes);
      setSaved(cfg.timeout_minutes);
    } catch (e) {
      toast.error("Could not load idle lock setting");
    } finally {
      setLoading(false);
    }
  }, [token, userToken]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await adminIdleConfigSet(token || null, userToken || null, value);
      setSaved(value);
      toast.success(`Idle lock set to ${value} minutes`);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) toast.error("Owner-only — you don't have permission to change this");
      else toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [token, userToken, value]);

  const dirty = value !== saved;

  return (
    <section
      className="border border-[#2D2A26] bg-[#0d0c0a] p-5"
      data-testid="admin-idle-lock-settings"
    >
      <div className="flex items-center gap-2 mb-1">
        <Lock size={14} className="text-[#D92D20]" />
        <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D92D20]">
          Admin Idle Lock Timeout
        </h3>
      </div>
      <p className="text-[12px] text-[#A39E93] font-mono mb-4 leading-relaxed">
        Automatic lock for inactive admin sessions. A 60-second warning is shown
        before the session is cleared and redirected to /admin/login. Owner-only.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-[12px] text-[#A39E93] font-mono">
          <Loader2 size={14} className="animate-spin" /> Loading current setting…
        </div>
      ) : (
        <>
          <div
            className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4"
            role="radiogroup"
            aria-label="Idle lock timeout"
          >
            {OPTIONS.map((opt) => {
              const active = value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setValue(opt.value)}
                  data-testid={`admin-idle-option-${opt.value}`}
                  className={
                    "text-left p-3 border transition-colors " +
                    (active
                      ? "border-[#D92D20] bg-[#1a0808] text-[#D92D20]"
                      : "border-[#2D2A26] text-[#A39E93] hover:border-[#D92D20] hover:text-[#D92D20]")
                  }
                >
                  <div className="font-mono text-[12px] uppercase tracking-[0.2em] flex items-center gap-1">
                    {active && <ShieldCheck size={12} />}
                    {opt.label}
                  </div>
                  <div className="text-[10px] mt-1 font-mono opacity-80">{opt.hint}</div>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] font-mono text-[#757065]">
              Current: <span className="text-[#A39E93]">{saved} min</span>
              {dirty && <span className="ml-2 text-[#E0A526]">· unsaved change</span>}
            </span>
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              data-testid="admin-idle-save"
              className="px-4 py-2 border border-[#D92D20] text-[#D92D20] font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-[#D92D20] hover:text-[#0a0907] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default AdminIdleLockSettings;
